import { canManageLama as canManageLamaRoles } from "@playbookai/types";
import type { IdTokenClaims } from "./auth-store.js";

/* Workspace-admin UI gating (PB-152, activated by PB-5).
 *
 * DEFAULT-DENY CONTRACT: only an explicit tenant-admin role grants admin
 * surfaces. The BFF `GET /api/auth/me` now ships `realm_access.roles` (PB-5),
 * so empty roles means "not an admin" (or a pre-PB-5 BFF) and we fail closed.
 * (Before PB-5 this gate failed open, because /me carried no roles and hiding
 * admin surfaces on empty roles would have locked everyone out.)
 *
 * Server-side authorization remains AUTHORITATIVE (root CLAUDE.md): this
 * gate is cosmetic UI hiding only and must never be the security boundary.
 */

/** Single source for admin-role normalization — both spellings exist in
 * backend precedent (see playbook-be collab-projections). Membership checks
 * go through this list, never inline the strings elsewhere. */
export const WORKSPACE_ADMIN_ROLES = ["tenant-admin", "tenant_admin"] as const;

export function canAdministerWorkspace(claims: IdTokenClaims | null): boolean {
  if (!claims) return false;
  // Default-deny: empty roles → no admin surfaces (PB-5). Server-side authz is
  // authoritative; this only hides UI.
  return claims.roles.some((role) =>
    (WORKSPACE_ADMIN_ROLES as readonly string[]).includes(role),
  );
}

/** The roles that govern KPIs. A UNION, and never an implication: ADR-0078
 *  keeps tenant roles additive, so an admin who is not a lead governs here and
 *  a lead who is not an admin governs here too. Amended for PB-5189 — the role
 *  was LAMA-only before. */
export const KPI_GOVERNANCE_ROLES = [
  "tenant-admin",
  "tenant_admin",
  "transformation-lead",
] as const;

/**
 * Whether the signed-in user may govern KPIs — create, edit metadata, assign
 * owner, activate, archive, restore.
 *
 * Separate from `canAdministerWorkspace` because the two answers have come
 * apart: `assertMayGovernKpis` on the server accepts a Transformation Lead,
 * and gating the UI on workspace-admin alone left that role with an API
 * permission it could not reach in the product. Affordance-only; the server
 * decides.
 */
export function canGovernKpis(claims: IdTokenClaims | null): boolean {
  if (!claims) return false;
  return claims.roles.some((role) =>
    (KPI_GOVERNANCE_ROLES as readonly string[]).includes(role),
  );
}

/**
 * Whether the session may MANAGE LAMA — questionnaires, campaigns, reports
 * (ADR-0078). Exact-role semantics through the shared `canManageLama`: only
 * the literal `transformation-lead` counts, and `tenant-admin` is deliberately
 * NOT an alias — the two additive roles are independent, so an administrator
 * without the lead role is denied LAMA management like any other member.
 * No snake-case tolerance either: nothing has ever emitted one for this role.
 *
 * Cosmetic UI gating only; `TransformationLeadGuard` in playbook-be is the
 * authoritative gate.
 */
export function canManageLama(claims: IdTokenClaims | null): boolean {
  return claims ? canManageLamaRoles(claims.roles) : false;
}
